'use client';

import { useCallback, useEffect, useState } from 'react';
import { PAGE_SIZE } from '../constants';

type UseInfiniteScrollParams = {
  totalCount: number;
  searchParamsKey: string;
};

type UseInfiniteScrollReturn = {
  sentinelRef: (node: HTMLDivElement | null) => void;
  visibleCount: number;
};

function useInfiniteScroll({ totalCount, searchParamsKey }: UseInfiniteScrollParams): UseInfiniteScrollReturn {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinel(node);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisibleCount(PAGE_SIZE);
  }, [searchParamsKey]);

  useEffect(() => {
    if (!sentinel || visibleCount >= totalCount) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount(prev => Math.min(prev + PAGE_SIZE, totalCount));
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, visibleCount, totalCount]);

  return {
    sentinelRef,
    visibleCount,
  };
}

export { useInfiniteScroll };
export type { UseInfiniteScrollParams, UseInfiniteScrollReturn };
